import { WidgetFrame, WidgetRow, WidgetStat } from '@/components/widgets/WidgetFrame';
import { InspectionBadge } from '@/components/StatusBadge';
import { millionWon, percent, periodLabel, shortDate, today, won } from '@/lib/format';
import { countableLeaves, isBehind, rollupProgress } from '@/lib/progress';
import {
  drawingTodo,
  inspectionTodo,
  ncrTodo,
  overdueSafetyActions,
  purchaseTodo,
  safetyActionTodo,
  testReportTodo,
  todayTbm,
} from '@/lib/todo';
import {
  useClaims,
  useDailyLogs,
  useDocuments,
  useDrawings,
  useEquipmentLogs,
  useInspections,
  useLabor,
  useNcrs,
  usePurchaseOrders,
  useSafetyInspections,
  useSiteProgress,
  useSubcontracts,
  useSupervisionLogs,
  useTbm,
  useTestReports,
  useWorkers,
} from '@/lib/queries';
import type { Uuid } from '@/lib/types';

/*
  위젯은 서로를 기다리지 않는다 — 각자 자기 쿼리를 갖는다({@link WidgetFrame}).
  그래서 여기서는 훅을 위로 끌어올리지 않고 위젯마다 다시 부른다. react-query 가 같은 키를
  합쳐 주므로 요청이 늘지는 않는다.

  「할 일」을 세는 규칙은 `lib/todo.ts` 에 있다 — 사이드바 「오늘」 묶음의 배지가 같은 함수를
  부르므로 위젯의 숫자와 메뉴의 숫자가 늘 같다.

  위젯의 목록은 `widgets/registry.ts` 한 곳이다. 여기는 그리는 쪽이고, 누가 볼 수 있는지·
  기본 크기·순서는 등록부가 정한다.
*/

/** 등록부가 모든 위젯에 건네는 것. 협력사·감리 분기는 위젯 안에서 한다. */
export type WidgetProps = { siteId: Uuid; judge: boolean };

/**
 * 전체 공정율 — 현장이 지금 어디까지 왔나.
 *
 * <p>협력사도 감리도 <b>같은 값</b>을 본다. 「내가 할 일」이 아니라 현장의 상태라 갈릴 것이 없다.
 *
 * <p>목록에서는 지연 공정 수만 센다. 어느 공정이 늦었는지는 화면에서 보는 것이고, 위젯은
 * 「봐야 하는가」에만 답하면 된다.
 */
export function ProgressWidget({ siteId }: { siteId: Uuid }) {
  const query = useSiteProgress(siteId);
  const rows = query.data ?? [];

  const rollup = rollupProgress(rows);
  const behind = countableLeaves(rows).filter(isBehind);
  const late = rollup.actualRate !== null && rollup.plannedRate !== null && rollup.actualRate < rollup.plannedRate;

  return (
    <WidgetFrame
      title="공정"
      icon="trending"
      to={`/sites/${siteId}/progress`}
      pending={query.isPending}
      error={query.error}
      empty={rollup.countedItems === 0}
      emptyText="기록된 공정 실적이 없습니다."
      headerRight={behind.length > 0 ? <span className="badge badge-red">지연 {behind.length}</span> : undefined}
    >
      <div className="flex flex-col gap-3">
        <WidgetStat
          label={late ? '전체 공정율 · 계획 미달' : '전체 공정율'}
          value={percent(rollup.actualRate)}
          sub={`계획 ${percent(rollup.plannedRate)}`}
        />
        <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
          {behind.slice(0, 3).map((row) => (
            <WidgetRow
              key={row.id}
              to={`/sites/${siteId}/progress`}
              title={row.name}
              meta={`${percent(row.actualRate)} / ${percent(row.plannedRate)}`}
            />
          ))}
        </div>
      </div>
    </WidgetFrame>
  );
}

/** 처리할 검측. 협력사는 「내가 답할 것」, 감리는 「내가 판정할 것」이 위로 온다. */
export function InspectionWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = useInspections(siteId);
  const rows = query.data ?? [];

  const todo = inspectionTodo(rows, judge);

  const shown = (todo.length > 0 ? todo : rows).slice(0, 4);

  return (
    <WidgetFrame
      title={judge ? '판정할 검측' : '검측'}
      icon="check"
      to={`/sites/${siteId}/inspections`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="검측 요청이 없습니다."
      headerRight={
        todo.length > 0 ? <span className="badge badge-blue">{todo.length}</span> : undefined
      }
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/inspections/${row.id}`}
            title={row.roundNo > 1 ? `${row.title} (${row.roundNo}차)` : row.title}
            trailing={<InspectionBadge status={row.status} />}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/** 기성. 최근 회차의 금액과 누계율이 요점이다. */
export function ClaimWidget({ siteId }: { siteId: Uuid }) {
  const query = useClaims(siteId);
  const rows = query.data ?? [];
  const latest = rows.slice().sort((a, b) => b.claimSeq - a.claimSeq)[0];

  return (
    <WidgetFrame
      title="기성"
      icon="chart"
      to={`/sites/${siteId}/claims`}
      pending={query.isPending}
      error={query.error}
      empty={latest === undefined}
      emptyText="청구 내역이 없습니다."
    >
      {latest !== undefined && (
        <div className="flex flex-col gap-3">
          <WidgetStat
            label={`${latest.claimSeq}회차 · ${periodLabel(latest.periodYm)}`}
            value={won(latest.claimAmount)}
            sub={latest.claimRate !== null ? `누계 ${percent(latest.claimRate)}` : undefined}
          />
          <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
            {rows
              .slice()
              .sort((a, b) => b.claimSeq - a.claimSeq)
              .slice(1, 3)
              .map((row) => (
                <WidgetRow
                  key={row.id}
                  title={`${row.claimSeq}회차`}
                  meta={won(row.claimAmount)}
                />
              ))}
          </div>
        </div>
      )}
    </WidgetFrame>
  );
}

/** 오늘 출역. 오늘 줄이 없으면 가장 최근 날짜를 보여 준다 — 빈 칸보다 낫다. */
export function LaborWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = useLabor(siteId);
  const rows = query.data ?? [];

  const target = rows.some((row) => row.logDate === today())
    ? today()
    : rows[0]?.logDate ?? null;
  const sameDay = rows.filter((row) => row.logDate === target);
  const total = sameDay.reduce((sum, row) => sum + row.headcount, 0);

  return (
    <WidgetFrame
      title={judge ? '현장 출역' : '출역'}
      icon="users"
      to={`/sites/${siteId}/labor`}
      pending={query.isPending}
      error={query.error}
      empty={target === null}
      emptyText="출역 기록이 없습니다."
    >
      <div className="flex flex-col gap-3">
        <WidgetStat
          label={target === today() ? '오늘' : shortDate(target)}
          value={`${total}명`}
        />
        <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
          {sameDay.slice(0, 3).map((row) => (
            <WidgetRow
              key={row.id}
              title={row.workArea ?? row.trade ?? '구역 미지정'}
              meta={`${row.headcount}명`}
            />
          ))}
        </div>
      </div>
    </WidgetFrame>
  );
}

/** 작업일보. 오늘 것이 있는지, 제출됐는지가 요점이다. */
export function DailyLogWidget({ siteId }: { siteId: Uuid }) {
  const query = useDailyLogs(siteId);
  const rows = query.data ?? [];
  const latest = rows[0];

  return (
    <WidgetFrame
      title="작업일보"
      icon="clipboard"
      to={`/sites/${siteId}/daily`}
      pending={query.isPending}
      error={query.error}
      empty={latest === undefined}
      emptyText="작성된 일보가 없습니다."
      headerRight={
        latest !== undefined ? (
          <span className={`badge ${latest.status === 'SUBMITTED' ? 'badge-gray' : 'badge-blue'}`}>
            {latest.status === 'SUBMITTED' ? '제출됨' : '작성 중'}
          </span>
        ) : undefined
      }
    >
      {latest !== undefined && (
        <div className="flex flex-col gap-2">
          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
            {shortDate(latest.logDate)}
            {latest.manpowerCount !== null && ` · 총 ${latest.manpowerCount}명`}
          </div>
          {latest.todayWork !== null && <p className="text-ui-sm">{latest.todayWork}</p>}
          <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
            {latest.items.slice(0, 3).map((item) => (
              <WidgetRow
                key={item.id}
                title={item.wbsItemName}
                meta={
                  item.quantity !== null
                    ? `${item.quantity.toLocaleString('ko-KR')}${item.unit ?? ''}`
                    : `${item.progressRate ?? 0}%`
                }
              />
            ))}
          </div>
        </div>
      )}
    </WidgetFrame>
  );
}

/** 받은 문서. 안 읽은 것이 위로 온다 — 열람 증적이 남는 자리다(DOC-82 §7). */
export function DocumentWidget({ siteId }: { siteId: Uuid }) {
  const query = useDocuments(siteId);
  const rows = query.data ?? [];
  const unread = rows.filter((row) => row.viewedAt === null);
  const shown = [...unread, ...rows.filter((row) => row.viewedAt !== null)].slice(0, 4);

  return (
    <WidgetFrame
      title="문서"
      icon="news"
      to={`/sites/${siteId}/documents`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="받은 문서가 없습니다."
      headerRight={unread.length > 0 ? <span className="badge badge-blue">{unread.length}</span> : undefined}
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/documents`}
            title={row.fileName}
            meta={row.viewedAt === null ? '새 문서' : shortDate(row.createdAt)}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/**
 * 안전 — 오늘 TBM 서명이 요점이다.
 *
 * <p>다른 위젯과 달리 **서명하지 않았으면 눈에 띄어야 한다.** 안전은 「나중에 봐도 되는 것」이
 * 아니라 작업 전에 끝나야 하는 것이라, 안 한 상태를 붉게 표시한다.
 */
export function SafetyWidget({ siteId }: { siteId: Uuid }) {
  const query = useTbm(siteId);
  const rows = query.data ?? [];
  const tbm = todayTbm(rows);

  return (
    <WidgetFrame
      title="안전"
      icon="lock"
      to={`/sites/${siteId}/safety`}
      pending={query.isPending}
      error={query.error}
      empty={tbm === undefined}
      emptyText="오늘 등록된 TBM 이 없습니다."
      headerRight={
        tbm !== undefined && !tbm.signedByMe ? (
          <span className="badge badge-red">서명 필요</span>
        ) : undefined
      }
    >
      {tbm !== undefined && (
        <div className="flex flex-col gap-2">
          <WidgetStat
            label="오늘 TBM 서명"
            value={`${tbm.signedCount}/${tbm.requiredCount}`}
            sub={tbm.signedByMe ? '서명했습니다' : '아직 서명하지 않았습니다'}
          />
          <p className="border-t pt-2 text-ui-xs" style={{ borderColor: 'var(--border)', color: 'var(--danger)' }}>
            {tbm.hazards}
          </p>
        </div>
      )}
    </WidgetFrame>
  );
}

/** 투입 인력 — 안전교육·건강진단이 빠진 사람이 있으면 그것이 먼저다. */
export function WorkerWidget({ siteId }: { siteId: Uuid }) {
  const query = useWorkers(siteId);
  const rows = query.data ?? [];
  const active = rows.filter((row) => row.leftOn === null);
  const missing = active.filter((row) => !row.safetyEducated || !row.healthChecked);

  return (
    <WidgetFrame
      title="투입 인력"
      icon="users"
      to={`/sites/${siteId}/labor?tab=workers`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="등록된 인력이 없습니다."
      headerRight={missing.length > 0 ? <span className="badge badge-red">{missing.length}</span> : undefined}
    >
      <div className="flex flex-col gap-3">
        <WidgetStat
          label="현재 투입"
          value={`${active.length}명`}
          sub={missing.length > 0 ? `서류 미비 ${missing.length}명` : undefined}
        />
        <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
          {(missing.length > 0 ? missing : active).slice(0, 3).map((row) => (
            <WidgetRow
              key={row.id}
              title={row.name}
              meta={
                !row.safetyEducated ? '안전교육' : !row.healthChecked ? '건강진단' : (row.jobTitle ?? '')
              }
            />
          ))}
        </div>
      </div>
    </WidgetFrame>
  );
}

/** 시공도·자재 승인 — 감리는 검토할 것, 협력사는 반려된 것이 위로 온다. */
export function DrawingWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = useDrawings(siteId);
  const rows = query.data ?? [];

  const todo = drawingTodo(rows, judge);
  const shown = (todo.length > 0 ? todo : rows).slice(0, 4);

  return (
    <WidgetFrame
      title="시공도·자재"
      icon="grid"
      to={`/sites/${siteId}/drawings`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="제출된 도면이 없습니다."
      headerRight={todo.length > 0 ? <span className="badge badge-blue">{todo.length}</span> : undefined}
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/drawings`}
            title={`${row.drawingNo} ${row.title}`}
            meta={`Rev.${row.revision}`}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/** 감리일지 — 지시사항이 협력사가 읽어야 할 부분이다. */
export function SupervisionWidget({ siteId }: { siteId: Uuid }) {
  const query = useSupervisionLogs(siteId);
  const rows = query.data ?? [];
  const latest = rows[0];

  return (
    <WidgetFrame
      title="감리일지"
      icon="clipboard"
      to={`/sites/${siteId}/supervision`}
      pending={query.isPending}
      error={query.error}
      empty={latest === undefined}
      emptyText="작성된 감리일지가 없습니다."
    >
      {latest !== undefined && (
        <div className="flex flex-col gap-2">
          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
            {shortDate(latest.logDate)}
            {latest.author !== null && ` · ${latest.author}`}
          </div>
          {latest.progressNote !== null && <p className="text-ui-sm">{latest.progressNote}</p>}
          {latest.instruction !== null && (
            <div className="rounded-md p-2" style={{ background: 'var(--accent-bg)' }}>
              <div className="mb-0.5 text-ui-2xs font-medium" style={{ color: 'var(--accent)' }}>지시사항</div>
              <p className="text-ui-sm">{latest.instruction}</p>
            </div>
          )}
        </div>
      )}
    </WidgetFrame>
  );
}

/**
 * 안전 점검 — 시정 지시가 요점이다.
 *
 * <p>회차가 아니라 <b>지적 줄</b>을 센다. 「점검 2회」는 아무것도 알려 주지 않지만 「기한 넘긴
 * 지적 1건」은 오늘 할 일이다. 기한을 넘긴 것은 붉게 센다.
 */
export function SafetyInspectionWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = useSafetyInspections(siteId);
  const rows = query.data ?? [];
  const actions = rows.flatMap((round) => round.actions);

  const todo = safetyActionTodo(rows, judge);
  const overdue = overdueSafetyActions(rows);
  const shown = (todo.length > 0 ? todo : actions).slice(0, 4);

  return (
    <WidgetFrame
      title="안전 점검"
      icon="alert"
      to={`/sites/${siteId}/safety-inspections`}
      pending={query.isPending}
      error={query.error}
      empty={actions.length === 0}
      emptyText="지적사항이 없습니다."
      headerRight={
        overdue.length > 0 ? (
          <span className="badge badge-red">기한 초과 {overdue.length}</span>
        ) : todo.length > 0 ? (
          <span className="badge badge-blue">{todo.length}</span>
        ) : undefined
      }
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/safety-inspections`}
            title={row.finding}
            meta={shortDate(row.dueDate)}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/** 부적합 — 감리는 회신을 확인할 것, 협력사는 회신해야 할 것이 위로 온다. */
export function NcrWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = useNcrs(siteId);
  const rows = query.data ?? [];

  const todo = ncrTodo(rows, judge);
  const shown = (todo.length > 0 ? todo : rows).slice(0, 4);

  return (
    <WidgetFrame
      title="부적합"
      icon="flag"
      to={`/sites/${siteId}/ncrs`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="발행된 부적합이 없습니다."
      headerRight={
        todo.length > 0 ? (
          <span className={`badge ${judge ? 'badge-blue' : 'badge-red'}`}>{todo.length}</span>
        ) : undefined
      }
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/ncrs`}
            title={row.title}
            meta={row.ncrNo.slice(-3)}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/** 시험성적서 — 불합격이 먼저다. 감리에게는 판정 대기가 할 일이다. */
export function TestReportWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = useTestReports(siteId);
  const rows = query.data ?? [];

  const todo = testReportTodo(rows, judge);
  const shown = (todo.length > 0 ? todo : rows).slice(0, 4);

  return (
    <WidgetFrame
      title="시험성적서"
      icon="flask"
      to={`/sites/${siteId}/test-reports`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="등록된 성적서가 없습니다."
      headerRight={
        todo.length > 0 ? (
          <span className={`badge ${judge ? 'badge-blue' : 'badge-red'}`}>{todo.length}</span>
        ) : undefined
      }
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/test-reports`}
            title={`${row.testType} ${row.target}`}
            meta={row.resultValue ?? shortDate(row.sampledOn)}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/** 구매·납품 — 협력사는 남은 수량, 감리는 검수 대기가 할 일이다. */
export function PurchaseWidget({ siteId, judge }: { siteId: Uuid; judge: boolean }) {
  const query = usePurchaseOrders(siteId);
  const rows = query.data ?? [];

  const todo = purchaseTodo(rows, judge);
  const shown = (todo.length > 0 ? todo : rows).slice(0, 4);

  return (
    <WidgetFrame
      title="구매·납품"
      icon="truck"
      to={`/sites/${siteId}/purchase`}
      pending={query.isPending}
      error={query.error}
      empty={rows.length === 0}
      emptyText="수신된 발주가 없습니다."
      headerRight={todo.length > 0 ? <span className="badge badge-blue">{todo.length}</span> : undefined}
    >
      <div className="flex flex-col">
        {shown.map((row) => (
          <WidgetRow
            key={row.id}
            to={`/sites/${siteId}/purchase`}
            title={row.itemName}
            meta={`${row.deliveredQuantity.toLocaleString('ko-KR')}/${row.quantity.toLocaleString('ko-KR')}${row.unit}`}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}

/** 장비 가동 — 오늘 몇 시간 돌았나. 확인 안 난 줄이 있으면 그 수를 함께 센다. */
export function EquipmentWidget({ siteId }: { siteId: Uuid }) {
  const query = useEquipmentLogs(siteId);
  const rows = query.data ?? [];

  const target = rows.some((row) => row.logDate === today()) ? today() : rows[0]?.logDate ?? null;
  const sameDay = rows.filter((row) => row.logDate === target);
  const hours = sameDay.reduce((sum, row) => sum + row.operatingHours, 0);
  const unconfirmed = rows.filter((row) => row.confirmedOn === null);

  return (
    <WidgetFrame
      title="장비 가동"
      icon="wrench"
      to={`/sites/${siteId}/equipment`}
      pending={query.isPending}
      error={query.error}
      empty={target === null}
      emptyText="등록된 가동일보가 없습니다."
      headerRight={unconfirmed.length > 0 ? <span className="badge badge-blue">미확인 {unconfirmed.length}</span> : undefined}
    >
      <div className="flex flex-col gap-3">
        <WidgetStat
          label={target === today() ? '오늘' : shortDate(target)}
          value={`${hours}시간`}
          sub={`장비 ${sameDay.length}대`}
        />
        <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
          {sameDay.slice(0, 3).map((row) => (
            <WidgetRow key={row.id} title={row.equipmentName} meta={`${row.operatingHours}시간`} />
          ))}
        </div>
      </div>
    </WidgetFrame>
  );
}

/** 계약. 협력사에게만 보인다 — 감리에게는 남의 돈이다. */
export function ContractWidget({ siteId }: { siteId: Uuid }) {
  const query = useSubcontracts(siteId);
  const rows = query.data ?? [];
  const main = rows[0];

  return (
    <WidgetFrame
      title="계약"
      icon="layout"
      to={`/sites/${siteId}/contract`}
      pending={query.isPending}
      error={query.error}
      empty={main === undefined}
      emptyText="등록된 계약이 없습니다."
    >
      {main !== undefined && (
        <div className="flex flex-col gap-3">
          <WidgetStat
            label={main.workName}
            value={millionWon(main.amountWithVat)}
            sub={main.contractNo ?? undefined}
          />
          <div className="flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
            <WidgetRow title="착공" meta={shortDate(main.startDate)} />
            <WidgetRow title="준공" meta={shortDate(main.endDate)} />
          </div>
        </div>
      )}
    </WidgetFrame>
  );
}
